import { useEffect } from 'react';
import { edgeVelocity, canScroll } from '../utils/dragAutoScroll';

// Continuous edge auto-scroll while an HTML5 drag is in progress (Scrolling Kanban #491).
//
// The browser's own drag auto-scroll is not usable for the board: Chromium scrolls a few
// pixels when the pointer enters a thin strip at the scroller's edge and then stops, and
// never scrolls the per-column ticket lists. This hook tracks the pointer through
// document-level `dragover` events (fired continuously by the browser while dragging, even
// when the pointer is still) and runs a requestAnimationFrame loop that keeps scrolling
// while the pointer stays in an edge zone:
//   - horizontally: `scrollerRef` (the Kanban scroller), zone inside its left/right edges,
//     also when the pointer has drifted past the edge (full speed);
//   - vertically (only when `vertical`): the element matching `verticalSelector` under the
//     pointer's x (a column's ticket list), zone inside its top/bottom edges.
// The loop stops on drop / dragend or when `active` turns false.
export default function useDragAutoScroll(scrollerRef, active, {
    vertical = false,
    verticalSelector = '[data-testid="column-ticket-list"]',
} = {}) {
    useEffect(() => {
        if (!active) return undefined;
        const scroller = scrollerRef.current;
        if (!scroller) return undefined;
        let pointer = null;
        let raf = 0;
        let lastTs = 0;

        const tick = (ts) => {
            raf = 0;
            if (!pointer) return;
            // Velocities are px per 60fps frame; scale by the real frame time (capped) so the
            // speed doesn't depend on the frame rate (throttled/slow machines scroll as fast).
            const frames = lastTs ? Math.min(4, Math.max(0.25, (ts - lastTs) / (1000 / 60))) : 1;
            lastTs = ts;
            const { x, y } = pointer;
            let moved = false;
            const r = scroller.getBoundingClientRect();
            // Only scroll horizontally while the pointer is level with the board.
            if (y >= r.top && y <= r.bottom) {
                const vx = edgeVelocity(x, r.left, r.right, window.innerWidth);
                if (vx && canScroll(vx, scroller.scrollLeft, scroller.scrollWidth, scroller.clientWidth)) {
                    scroller.scrollLeft += Math.round(vx * frames);
                    moved = true;
                }
            }
            if (vertical) {
                const lists = scroller.querySelectorAll(verticalSelector);
                for (const list of lists) {
                    const lr = list.getBoundingClientRect();
                    if (x < lr.left || x > lr.right) continue;
                    // A little slack above/below so the zone also catches the column header
                    // and the Add-ticket row (the pointer naturally overshoots the list).
                    if (y < lr.top - 48 || y > lr.bottom + 48) break;
                    const vy = edgeVelocity(y, lr.top, lr.bottom, window.innerHeight);
                    if (vy && canScroll(vy, list.scrollTop, list.scrollHeight, list.clientHeight)) {
                        list.scrollTop += Math.round(vy * frames);
                        moved = true;
                    }
                    break;
                }
            }
            // Keep looping while something scrolled; the next dragover restarts it otherwise.
            if (moved) raf = window.requestAnimationFrame(tick);
            else lastTs = 0;
        };

        const onDragOver = (e) => {
            pointer = { x: e.clientX, y: e.clientY };
            if (!raf) raf = window.requestAnimationFrame(tick);
        };
        const stop = () => {
            pointer = null;
            lastTs = 0;
            if (raf) window.cancelAnimationFrame(raf);
            raf = 0;
        };

        document.addEventListener('dragover', onDragOver, true);
        document.addEventListener('drop', stop, true);
        document.addEventListener('dragend', stop, true);
        return () => {
            stop();
            document.removeEventListener('dragover', onDragOver, true);
            document.removeEventListener('drop', stop, true);
            document.removeEventListener('dragend', stop, true);
        };
    }, [scrollerRef, active, vertical, verticalSelector]);
}
